/**
 * Destination knowledge base: the admin-only record of what Ken knows about a
 * place. A destination carries a fact sheet (evergreen metadata like which
 * airport to fly into and whether rideshare works), guide tips tagged by
 * audience, places worth recommending, and the sources they came from.
 *
 * Everything an ingestion run produces lands as PROPOSED and only becomes
 * ACTIVE when accepted in the review tab. Types are `Kb`-prefixed so they
 * read apart from the trip and lead types.
 *
 * Audience tags reuse the lead intake option values (party type, travel level,
 * tempo, interests) so a client's travel profile maps onto guide tips as a
 * plain set intersection. A web test keeps `KB_AUDIENCE_TAGS` in step with
 * `wanderlust-cravings-site/src/lib/leadOptions.ts`.
 */
export const KB_DESTINATION_KINDS = ['city', 'region', 'country'];
export const KB_CONTENT_STATUSES = ['PROPOSED', 'ACTIVE', 'ARCHIVED'];
export const KB_ENTRY_KINDS = ['hotel', 'restaurant', 'activity', 'sight', 'neighborhood', 'other'];
export const KB_PRICE_BANDS = ['budget', 'midrange', 'upscale', 'luxury'];
/**
 * Kind-specific extras stored in `KbEntry.details`. Keys are restricted so the
 * extractor and the edit dialog agree on what an entry can carry.
 */
export const KB_ENTRY_DETAIL_KEYS = {
    hotel: ['style', 'room_tip'],
    restaurant: ['cuisine', 'meal', 'reservation_tip'],
    activity: ['provider', 'duration', 'meeting_point'],
    sight: ['duration', 'ticket_tip'],
    neighborhood: ['vibe'],
    other: [],
};
/** The fact sheet keys, in the order the sheet renders them. */
export const KB_FACT_KEYS = [
    'fly_into',
    'rideshare',
    'apps',
    'transit',
    'currency',
    'tipping',
    'language',
    'plugs',
    'tap_water',
    'best_time',
    'connectivity',
    'safety',
    'visa',
    'other',
];
/** One line per key describing what belongs there. Shown as help text and injected into the extraction prompt. */
export const KB_FACT_KEY_GUIDE = {
    fly_into: 'Which airport(s) to fly into and how to get from there to the center',
    rideshare: 'Whether Uber, Lyft, Bolt or a local app works, and what to do about taxis',
    apps: 'Apps worth downloading before landing (transit tickets, maps, rides, delivery)',
    transit: 'How public transport works, which passes to buy, who rides free',
    currency: 'Currency, cash versus card, ATM and exchange advice',
    tipping: 'Tipping norms in restaurants, taxis and hotels',
    language: 'Languages spoken and how far English goes',
    plugs: 'Plug type and voltage',
    tap_water: 'Whether tap water is safe to drink',
    best_time: 'Best months to visit and which to avoid',
    connectivity: 'Roaming, SIM and eSIM options',
    safety: 'Common scams, pickpocket zones, anything worth a heads-up',
    visa: 'Entry requirements for US passport holders',
    other: 'Anything evergreen that fits nowhere else',
};
export const KB_TIP_SECTIONS = [
    'getting_there',
    'getting_around',
    'when_to_go',
    'where_to_stay',
    'food_scene',
    'with_kids',
    'money',
    'etiquette_safety',
    'packing',
    'other',
];
export const KB_AUDIENCE_PARTY_TYPES = ['SOLO', 'PARTNER', 'FRIENDS', 'FAMILY_WITH_KIDS', 'EXTENDED_GROUP'];
export const KB_AUDIENCE_TRAVEL_LEVELS = ['value_focused', 'comfortable_highlights', 'elevated_experience', 'luxury'];
export const KB_AUDIENCE_TEMPOS = ['SLOW', 'BALANCED', 'PACKED'];
export const KB_AUDIENCE_INTERESTS = [
    'food_dining',
    'nature_landscapes',
    'culture_history',
    'cities_shopping',
    'relaxation_wellness',
    'beaches_warm',
    'adventure_outdoor',
    'family_friendly',
    'luxury_stays',
    'local_immersion',
];
/** Tags that only exist here; the intake never asks about them. */
export const KB_AUDIENCE_EXTRAS = ['first_time', 'repeat_visitor', 'mobility_needs'];
export const KB_AUDIENCE_TAGS = [
    ...KB_AUDIENCE_PARTY_TYPES,
    ...KB_AUDIENCE_TRAVEL_LEVELS,
    ...KB_AUDIENCE_TEMPOS,
    ...KB_AUDIENCE_INTERESTS,
    ...KB_AUDIENCE_EXTRAS,
];
export const KB_SOURCE_KINDS = ['text', 'pdf', 'url'];
export const KB_SOURCE_STATUSES = ['processing', 'completed', 'failed'];
/** What an ingestion run should look for in a source. */
export const KB_LOOK_FOR = ['everything', 'places', 'facts_and_tips'];
/**
 * What to read a page for, by the kind of place it is. A country or region
 * page lists its regions rather than places worth booking, so only its facts
 * and tips are worth extracting; a city page is worth everything.
 */
export const KB_DEFAULT_LOOK_FOR = {
    country: 'facts_and_tips',
    region: 'facts_and_tips',
    city: 'everything',
};
export const KB_LINK_KINDS = ['wikivoyage', 'fora', 'official', 'other'];
